import { CookieHelper } from './cookies';
import { getReferrer } from './referrer';
import { UtmHelper } from './utm';

export type TrackingData = {
  submission_url: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_term?: string;
  utm_campaign?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  ours_device_id?: string;
  vwo_uuid?: string;
  campaignId?: string;
  templateId?: string;
};

export class TrackingHelper {
  /**
   * Get complete tracking data with UTM params and tracking cookies
   * Prioritizes URL parameters over cookie values
   * @param cookieString - Optional cookie string for server-side usage
   * @param requestUrl - Optional request URL for server-side usage
   */
  static getTrackingData(
    cookieString?: string,
    requestUrl?: string
  ): TrackingData {
    const utmParams = UtmHelper.getUtmParamsWithFallback(cookieString);

    const getQueryParam = (param: string, url?: string): string | undefined => {
      try {
        const search = url
          ? new URL(url).search
          : typeof window !== 'undefined'
            ? window.location.search
            : '';
        if (!search) return undefined;
        const params = new URLSearchParams(search);
        return params.get(param) || undefined;
      } catch {
        return undefined;
      }
    };

    const urlToUse =
      requestUrl || (typeof window !== 'undefined' ? window.location.href : '');

    return {
      submission_url: urlToUse,
      referrer: getReferrer(),
      utm_source: utmParams.utm_source || undefined,
      utm_medium: utmParams.utm_medium || undefined,
      utm_term: utmParams.utm_term || undefined,
      utm_campaign: utmParams.utm_campaign || undefined,
      utm_content: utmParams.utm_content || undefined,
      fbclid:
        getQueryParam('fbclid', urlToUse) ||
        this.getCookieValue('fbclid', cookieString),
      gclid:
        getQueryParam('gclid', urlToUse) ||
        this.getCookieValue('gclid', cookieString),
      msclkid:
        getQueryParam('msclkid', urlToUse) ||
        this.getCookieValue('msclkid', cookieString),
      ours_device_id:
        getQueryParam('ours_device_id', urlToUse) ||
        this.getCookieValue('ours_device_id', cookieString),
      vwo_uuid:
        getQueryParam('_vwo_uuid', urlToUse) ||
        this.getCookieValue('_vwo_uuid', cookieString),
      campaignId:
        getQueryParam('campaignId') ||
        this.getCookieValue('campaignId', cookieString),
      templateId:
        getQueryParam('templateId') ||
        this.getCookieValue('templateId', cookieString),
    };
  }

  /**
   * Get cookie value or undefined
   * @param cookieName - Name of the cookie
   * @param cookieString - Optional cookie string for server-side usage
   */
  private static getCookieValue(
    cookieName: string,
    cookieString?: string
  ): string | undefined {
    const cookies =
      cookieString || (typeof document !== 'undefined' ? document.cookie : '');
    if (!cookies) return undefined;

    const cookieHelper = new CookieHelper(cookies);
    return cookieHelper.get(cookieName)?.value || undefined;
  }

  /**
   * Get only UTM parameters (no tracking cookies)
   * @param cookieString - Optional cookie string for server-side usage
   */
  static getUtmTrackingData(
    cookieString?: string
  ): Pick<
    TrackingData,
    'utm_source' | 'utm_medium' | 'utm_term' | 'utm_campaign' | 'utm_content'
  > {
    const utmParams = UtmHelper.getUtmParamsWithFallback(cookieString);

    return {
      utm_source: utmParams.utm_source || undefined,
      utm_medium: utmParams.utm_medium || undefined,
      utm_term: utmParams.utm_term || undefined,
      utm_campaign: utmParams.utm_campaign || undefined,
      utm_content: utmParams.utm_content || undefined,
    };
  }
}
